/**
 * Convex Hooks for connecting UI to backend
 * Place this in hooks/ folder when created
 */

import { useQuery, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';

// Get all experiments for a teacher
export function useTeacherExperiments(teacherId: string | undefined) {
    return useQuery(
        api.experiments.getTeacherExperiments,
        teacherId ? { teacherId } : 'skip'
    );
}

// Get specific experiment details
export function useExperiment(experimentId: Id<"experimentConfigs"> | undefined) {
    return useQuery(
        api.experiments.getExperiment,
        experimentId ? { experimentId } : 'skip'
    );
}

// Get experiment by class code
export function useExperimentByClassCode(classCode: string | undefined) {
    return useQuery(
        api.experiments.getExperimentByClassCode,
        classCode ? { classCode } : 'skip'
    );
}

// Get all students for an experiment
export function useExperimentStudents(experimentId: Id<"experimentConfigs"> | undefined) {
    return useQuery(
        api.experiments.getExperimentStudents,
        experimentId ? { experimentConfigId: experimentId } : 'skip'
    );
}

// Get student's experiments
export function useStudentExperiments(studentId: string | undefined) {
    return useQuery(
        api.experiments.getStudentExperiments,
        studentId ? { studentId } : 'skip'
    );
}

// Get student progress for specific experiment
export function useStudentProgress(
    studentId: string | undefined,
    experimentId: Id<"experimentConfigs"> | undefined
) {
    return useQuery(
        api.experiments.getStudentProgress,
        studentId && experimentId ? { studentId, experimentConfigId: experimentId } : 'skip'
    );
}

// Get pre-lab questions
export function usePreLabQuestions(experimentId: Id<"experimentConfigs"> | undefined) {
    return useQuery(
        api.experiments.getPreLabQuestions,
        experimentId ? { experimentConfigId: experimentId } : 'skip'
    );
}

// Get discussion questions
export function useDiscussionQuestions(experimentId: Id<"experimentConfigs"> | undefined) {
    return useQuery(
        api.experiments.getDiscussionQuestions,
        experimentId ? { experimentConfigId: experimentId } : 'skip'
    );
}

// Get experiment statistics
export function useExperimentStats(experimentId: Id<"experimentConfigs"> | undefined) {
    return useQuery(
        api.experiments.getExperimentStats,
        experimentId ? { experimentConfigId: experimentId } : 'skip'
    );
}

// Mutations
export function useCreateExperiment() {
    return useMutation(api.experiments.createExperiment);
}

export function useUpdateExperimentConfig() {
    return useMutation(api.experiments.updateExperimentConfig);
}

export function useUpdatePreLabSettings() {
    return useMutation(api.experiments.updatePreLabSettings);
}

export function useRetakeExperiment() {
    return useMutation(api.experiments.retakeExperiment);
}

export function useSetTeacherPassword() {
    return useMutation(api.experiments.setTeacherPassword);
}

export function useAddPreLabQuestion() {
    return useMutation(api.experiments.addPreLabQuestion);
}

export function useAddDiscussionQuestion() {
    return useMutation(api.experiments.addDiscussionQuestion);
}

export function useDeletePreLabQuestion() {
    return useMutation(api.experiments.deletePreLabQuestion);
}

export function useDeleteExperiment() {
    return useMutation(api.experiments.deleteExperiment);
}

export function useJoinExperiment() {
    return useMutation(api.experiments.joinExperiment);
}

export function useSubmitPreLabAssessment() {
    return useMutation(api.experiments.submitPreLabAssessment);
}

export function useTeacherBypass() {
    return useMutation(api.experiments.teacherBypass);
}

export function useSaveLabData() {
    return useMutation(api.experiments.saveLabData);
}

export function useSubmitDiscussionAnswers() {
    return useMutation(api.experiments.submitDiscussionAnswers);
}

export function useGradeDiscussion() {
    return useMutation(api.experiments.gradeDiscussion);
}
